/**
 * Frame Profiler — instruments the render loop to find performance bottlenecks.
 * Enable with ?perf in the URL or by pressing F3 in-game.
 */

const SAMPLE_FRAMES = 60;

interface SectionTiming {
  name: string;
  total: number;
  max: number;
  samples: number;
}

import { isDevMode } from './devMode';
import { summariseFrames, frameSummaryLines, type FrameSamples } from './frameStats';

export class FrameProfiler {
  private sections: Map<string, SectionTiming> = new Map();
  private sectionOrder: string[] = [];
  private currentStart = 0;
  private frameStart = 0;
  private frameTotal = 0;
  private frameMax = 0;
  private frameCount = 0;
  private enabled = false;
  private overlay: HTMLDivElement | null = null;
  private gpu: { calls: number; triangles: number; programs: number; geometries: number; textures: number } | null = null;
  /** The real frame (rAF to rAF), the simulation's share and the heap — summarised by ui/frameStats.ts. */
  private lastBegin = 0;
  private samples: FrameSamples = { intervals: [], work: [], sim: [], ticks: [], simSeconds: 0, heap: [] };

  constructor() {
    const urlEnabled = window.location.search.includes('perf');
    if (urlEnabled) this.enable();

    window.addEventListener('keydown', (e) => {
      if (e.key === 'F3' && isDevMode()) {
        e.preventDefault();
        this.toggle();
      }
    });
  }

  toggle(): void {
    if (this.enabled) this.disable();
    else this.enable();
  }

  enable(): void {
    this.enabled = true;
    this.reset();
    this.createOverlay();
  }

  disable(): void {
    this.enabled = false;
    if (this.overlay) {
      this.overlay.remove();
      this.overlay = null;
    }
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Call at the start of animate() */
  beginFrame(): void {
    if (!this.enabled) return;
    this.frameStart = performance.now();
    if (this.lastBegin > 0) this.samples.intervals.push(this.frameStart - this.lastBegin);
    this.lastBegin = this.frameStart;
  }

  /** The simulation's part of this frame: ms inside the match's advance, the ticks it ran, their simulated seconds. */
  setSim(ms: number, ticks: number, seconds: number): void {
    if (!this.enabled) return;
    this.samples.sim.push(ms);
    this.samples.ticks.push(ticks);
    this.samples.simSeconds += seconds;
  }

  /** Start timing a named section */
  begin(_name: string): void {
    if (!this.enabled) return;
    this.currentStart = performance.now();
  }

  /** End timing the current section */
  end(name: string): void {
    if (!this.enabled) return;
    const elapsed = performance.now() - this.currentStart;
    let section = this.sections.get(name);
    if (!section) {
      section = { name, total: 0, max: 0, samples: 0 };
      this.sections.set(name, section);
      this.sectionOrder.push(name);
    }
    section.total += elapsed;
    if (elapsed > section.max) section.max = elapsed;
    section.samples++;
  }

  /** Record GPU stats from renderer.info (call once per frame before endFrame). */
  setRenderInfo(info: { render: { calls: number; triangles: number }; programs?: unknown[] | null; memory: { geometries: number; textures: number } }): void {
    if (!this.enabled) return;
    this.gpu = {
      calls: info.render.calls,
      triangles: info.render.triangles,
      programs: info.programs?.length ?? 0,
      geometries: info.memory.geometries,
      textures: info.memory.textures,
    };
  }

  /** Call at the end of animate() */
  endFrame(): void {
    if (!this.enabled) return;
    const elapsed = performance.now() - this.frameStart;
    this.samples.work.push(elapsed);
    const mem = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory;
    if (mem) this.samples.heap.push(mem.usedJSHeapSize);
    this.frameTotal += elapsed;
    if (elapsed > this.frameMax) this.frameMax = elapsed;
    this.frameCount++;

    if (this.frameCount >= SAMPLE_FRAMES) {
      this.flush();
    }
  }

  private reset(): void {
    this.sections.clear();
    this.sectionOrder = [];
    this.frameTotal = 0;
    this.frameMax = 0;
    this.frameCount = 0;
    this.samples = { intervals: [], work: [], sim: [], ticks: [], simSeconds: 0, heap: [] };
  }

  private flush(): void {
    const n = this.frameCount;

    const lines: string[] = frameSummaryLines(summariseFrames(this.samples), n);
    if (this.gpu) {
      lines.push(`GPU    draw calls ${this.gpu.calls}  tris ${(this.gpu.triangles / 1000).toFixed(0)}k  programs ${this.gpu.programs}  geoms ${this.gpu.geometries}  tex ${this.gpu.textures}`);
    }
    lines.push('─'.repeat(48));

    // Build bar chart
    for (const name of this.sectionOrder) {
      const s = this.sections.get(name)!;
      const avg = s.total / n;
      const max = s.max;
      const barLen = Math.round(avg * 10); // 1ms = 10 chars
      const bar = '█'.repeat(Math.min(barLen, 30));
      lines.push(
        `${name.padEnd(16)} ${avg.toFixed(2).padStart(6)}ms  max ${max.toFixed(1).padStart(5)}ms ${bar}`
      );
    }

    // Also log to console for copy-paste
    console.log(lines.join('\n'));

    // Update overlay
    if (this.overlay) {
      this.overlay.textContent = lines.join('\n');
    }

    this.reset();
  }

  private createOverlay(): void {
    if (this.overlay) return;
    this.overlay = document.createElement('div');
    Object.assign(this.overlay.style, {
      position: 'fixed',
      top: '10px',
      right: '10px',
      background: 'rgba(0, 0, 0, 0.85)',
      color: '#0f0',
      fontFamily: 'monospace',
      fontSize: '11px',
      padding: '8px 12px',
      borderRadius: '4px',
      whiteSpace: 'pre',
      zIndex: '99999',
      pointerEvents: 'none',
      lineHeight: '1.4',
    });
    this.overlay.textContent = 'Profiling... (collecting 60 frames)';
    document.body.appendChild(this.overlay);
  }
}
